import React, { useState, useEffect } from 'react';
import { CheckCircle, ChevronDown, ChevronRight, Cross, Lock, ArrowLeft } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import InteractiveLesson from './InteractiveLesson';
import ReviewSession from './ReviewSession';
import { completeCourseLesson } from './firebase/progressService';
import { getCurrentUser } from './firebase/authService';
import { scheduleReviews } from './services/reviewService';
import { lesson17Data, lesson18Data, lesson19Data, lesson20Data, lesson21Data, lesson22Data, lesson23Data, lesson24Data } from './interactiveLessonData';

const EarlyChristianityGuide = ({ userProgress, onNavigate, onGoBack, onProgressUpdate }) => {
  const location = useLocation();
  const [expandedLesson, setExpandedLesson] = useState(null);
  const [completedLessons, setCompletedLessons] = useState([]);
  const [interactiveMode, setInteractiveMode] = useState(null);
  const [reviewMode, setReviewMode] = useState(null);

  // Check for review mode from navigation state
  useEffect(() => {
    if (location.state?.reviewMode && location.state.reviewMode.path === 'earlyChristianity') {
      setReviewMode(location.state.reviewMode);
    }
  }, [location.state]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (userProgress?.courses?.earlyChristianity?.completedLessons) {
      setCompletedLessons(userProgress.courses.earlyChristianity.completedLessons);
    }
  }, [userProgress]);

  const getTotalXP = () => {
    return userProgress?.courses?.earlyChristianity?.totalXP || 0;
  };

  const lessons = [
    { number: 17, title: 'Before Denominations Existed', duration: '7 min', data: lesson17Data },
    { number: 18, title: 'The Apostolic Fathers', duration: '7 min', data: lesson18Data },
    { number: 19, title: 'How the Trinity Doctrine Formed', duration: '8 min', data: lesson19Data },
    { number: 20, title: 'The Nicene Creed', duration: '7 min', data: lesson20Data },
    { number: 21, title: 'Early Heresies and Why They Matter', duration: '7 min', data: lesson21Data },
    { number: 22, title: 'The Church Fathers', duration: '7 min', data: lesson22Data },
    { number: 23, title: 'Worship in the Early Church', duration: '6 min', data: lesson23Data },
    { number: 24, title: 'Lessons from Early Christianity', duration: '6 min', data: lesson24Data },
  ];

  const handleLessonComplete = async (lessonNumber, xp) => {
    const user = await getCurrentUser();
    if (!user) return;

    await completeCourseLesson(user.uid, 'earlyChristianity', lessonNumber, xp);
    await scheduleReviews(user.uid, 'earlyChristianity', lessonNumber);
    
    if (onProgressUpdate) {
      await onProgressUpdate();
    }

    setInteractiveMode(null);
    setExpandedLesson(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleReviewComplete = () => {
    setReviewMode(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (reviewMode) {
    const lesson = lessons.find(l => l.number === reviewMode.lessonNumber);
    if (lesson?.data) {
      return (
        <ReviewSession
          lessonData={lesson.data}
          onComplete={handleReviewComplete}
          onBack={handleReviewComplete}
        />
      );
    }
  }

  if (interactiveMode !== null) {
    const lesson = lessons.find(l => l.number === interactiveMode);
    if (lesson?.data) {
      return <InteractiveLesson lessonData={lesson.data} onComplete={(xp) => handleLessonComplete(interactiveMode, xp)} onExit={() => { setInteractiveMode(null); setExpandedLesson(null); }} />;
    }
  }

  const progressPercent = Math.round((completedLessons.length / lessons.length) * 100);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <div className="relative overflow-hidden text-white py-16 px-4">
        <div className="absolute inset-0 bg-gradient-to-br from-rose-500 via-pink-500 to-red-600"></div>
        <div className="relative max-w-4xl mx-auto z-10">
          <div className="flex items-center gap-3 mb-4">
            <Cross className="w-12 h-12" />
            <div>
              <h1 className="text-4xl md:text-5xl font-black">What Early Christians Believed</h1>
              <p className="text-rose-100">Before denominations and how the Trinity formed</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <button
          onClick={() => onGoBack ? onGoBack() : onNavigate('learn')}
          className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-xl border-2 border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition font-semibold"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Paths</span>
        </button>

        <div className="bg-white rounded-2xl border-2 border-slate-200 p-6 mb-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Your Progress</h2>
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1">
              <div className="flex justify-between text-sm mb-2">
                <span className="font-medium text-slate-700">Lessons Completed</span>
                <span className="font-bold text-rose-600">{completedLessons.length}/{lessons.length}</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-3">
                <div 
                  className="bg-gradient-to-r from-rose-500 to-pink-500 h-3 rounded-full transition-all"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-black text-rose-600">{getTotalXP()}</div>
              <div className="text-xs text-slate-600">Total XP</div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {lessons.map((lesson, index) => {
            const isCompleted = completedLessons.includes(lesson.number);
            const isLocked = index > 0 && !completedLessons.includes(lessons[index - 1].number);
            const isExpanded = expandedLesson === lesson.number;

            return (
              <div
                key={lesson.number}
                className={`bg-white rounded-2xl border-2 transition ${
                  isCompleted ? 'border-rose-400' : isLocked ? 'border-slate-200 opacity-60' : 'border-slate-200 hover:border-rose-300'
                }`}
              >
                <div 
                  className={`p-6 rounded-t-2xl transition ${isLocked ? 'cursor-not-allowed' : 'hover:bg-slate-50 cursor-pointer'}`}
                  onClick={() => !isLocked && setExpandedLesson(isExpanded ? null : lesson.number)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      <div className={`w-12 h-12 min-w-[3rem] rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0 ${
                        isCompleted ? 'bg-rose-100 text-rose-700' : isLocked ? 'bg-slate-200 text-slate-400' : 'bg-rose-100 text-rose-700'
                      }`}>
                        {isCompleted ? <CheckCircle className="w-6 h-6" /> : index + 1}
                      </div>
                      <div className="flex-1">
                        <h3 className={`font-bold text-lg ${isLocked ? 'text-slate-400' : 'text-slate-900'}`}>{lesson.title}</h3>
                        <p className={`text-sm ${isLocked ? 'text-slate-400' : 'text-slate-600'}`}>{lesson.duration}</p>
                      </div>
                    </div>
                    {isLocked ? (
                      <div className="flex items-center justify-center">
                        <Lock className="w-6 h-6 text-slate-400 flex-shrink-0" />
                      </div>
                    ) : (
                      isExpanded ? <ChevronDown className="w-6 h-6 text-slate-400" /> : <ChevronRight className="w-6 h-6 text-slate-400" />
                    )}
                  </div>
                </div>

                {isExpanded && !isLocked && (
                  <div className="border-t-2 border-slate-100 p-6 rounded-b-2xl">
                    <button
                      onClick={() => lesson.data ? setInteractiveMode(lesson.number) : null}
                      disabled={!lesson.data}
                      className={`w-full py-3 px-6 rounded-xl font-semibold transition ${
                        lesson.data 
                          ? 'bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:shadow-lg cursor-pointer'
                          : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                      }`}
                    >
                      {lesson.data ? (isCompleted ? 'Review Lesson' : 'Start Lesson') : 'Coming Soon'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default EarlyChristianityGuide;
